import { Component } from "./component-base.js";
import { QuantitySelectorComponent } from "./quantity-selector.js";
import { updateCartBubble } from "./utilities.js";

// Generic Drawer Component - Make it globally available
class DrawerComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.initialized) return;
    this.initialized = true;

    this.overlay = document.querySelector("[data-site-overlay]");
    this.toggleButtons = document.querySelectorAll(this.getToggleSelector());
    this.isOpen = !this.classList.contains(this.getAnimationType() === 'fade' ? "opacity-0" : "translate-x-full");
    this.closeButton = this.querySelector(this.getCloseSelector());

    this.#syncOverlayState();
    this.#addEventListeners();
    this.#setupSpecificListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeEventListeners();
  }

  // Methods to be overridden by child classes
  getToggleSelector() {
    return "[data-drawer-toggle]";
  }

  getCloseSelector() {
    return "[data-drawer-toggle]";
  }

  getClosedClass() {
    return this.getAnimationType() === 'fade'
      ? ["opacity-0", "pointer-events-none"]
      : ["translate-x-full"];
  }

  getOpenClass() {
    return this.getAnimationType() === 'fade'
      ? ["opacity-100", "pointer-events-auto"]
      : ["translate-x-0"];
  }

  getAnimationType() {
    return 'fade'; // Default to fade animation
  }

  getEventPrefix() {
    return "drawer";
  }

  #addEventListeners() {
    const prefix = this.getEventPrefix();
    // Bind methods to preserve context
    this.boundHandleOpen = this.handleOpen.bind(this);
    this.boundHandleClose = this.handleClose.bind(this);
    
    window.addEventListener(`${prefix}:drawer-open`, this.boundHandleOpen);
    window.addEventListener(`${prefix}:drawer-close`, this.boundHandleClose);
    window.addEventListener(`${prefix}:drawer-toggle`, this.#handleToggle);

    this.toggleButtons.forEach((btn) =>
      btn.addEventListener("click", this.#handleToggle)
    );

    if (this.closeButton) {
      this.closeButton.addEventListener("click", this.boundHandleClose);
    }

    if (this.overlay) {
      this.overlay.addEventListener("click", this.boundHandleClose);
    }

    // Close on escape key
    document.addEventListener("keydown", this.#handleKeydown);
  }

  #removeEventListeners() {
    const prefix = this.getEventPrefix();
    window.removeEventListener(`${prefix}:drawer-open`, this.boundHandleOpen);
    window.removeEventListener(`${prefix}:drawer-close`, this.boundHandleClose);
    window.removeEventListener(`${prefix}:drawer-toggle`, this.#handleToggle);

    this.toggleButtons.forEach((btn) =>
      btn.removeEventListener("click", this.#handleToggle)
    );

    if (this.closeButton) {
      this.closeButton.removeEventListener("click", this.boundHandleClose);
    }

    if (this.overlay) {
      this.overlay.removeEventListener("click", this.boundHandleClose);
    }

    document.removeEventListener("keydown", this.#handleKeydown);
  }

  #handleKeydown = (event) => {
    if (event.key === "Escape" && this.isOpen) {
      this.boundHandleClose();
    }
  };

  handleOpen() {
    this.isOpen = true;
    
    // Update ARIA attribute for accessibility
    this.setAttribute('aria-hidden', 'false');
    
    this.classList.remove(...this.getClosedClass());
    this.classList.add(...this.getOpenClass());

    if (this.overlay) {
      this.overlay.classList.remove("pointer-events-none", "opacity-0");
      this.overlay.classList.add("opacity-100");
    }

    // document.body.classList.add("overflow-hidden");
  }

  handleClose() {
    this.isOpen = false;
    
    // Remove focus from any focused element inside the drawer before hiding
    // This prevents the aria-hidden warning when a descendant has focus
    const focusedElement = this.querySelector(':focus');
    if (focusedElement) {
      focusedElement.blur();
      
      // Return focus to the first toggle button for better keyboard navigation
      // This helps users continue navigating from where they triggered the drawer
      if (this.toggleButtons.length > 0) {
        setTimeout(() => {
          this.toggleButtons[0].focus();
        }, 50);
      }
    }
    
    // Update ARIA attribute for accessibility
    this.setAttribute('aria-hidden', 'true');
    
    this.classList.remove(...this.getOpenClass());
    this.classList.add(...this.getClosedClass());

    if (this.overlay) {
      this.overlay.classList.remove("opacity-100");
      this.overlay.classList.add("opacity-0");
      setTimeout(() => {
        this.overlay?.classList.add("pointer-events-none");
      }, 300);
    }
    // document.body.classList.remove("overflow-hidden");
  }

  #handleToggle = () => {
    this.isOpen ? this.handleClose() : this.handleOpen();
  };

  #syncOverlayState() {
    if (!this.overlay) return;

    if (this.isOpen) {
      this.overlay.classList.remove("pointer-events-none", "opacity-0");
      this.overlay.classList.add("opacity-100");
    } else {
      this.overlay.classList.add("pointer-events-none", "opacity-0");
      this.overlay.classList.remove("opacity-100");
    }
  }

  // Hook for child classes to add specific functionality
  #setupSpecificListeners() {
    // Override in child classes
  }
}

// Header Drawer Component - extends generic drawer
class HeaderDrawerComponent extends DrawerComponent {
  getToggleSelector() {
    return "[data-header-drawer-toggle]";
  }

  getCloseSelector() {
    return "[data-header-drawer-toggle]";
  }

  getAnimationType() {
    return 'fade';
  }

  getEventPrefix() {
    return "header";
  }

  // Override handleOpen to announce header drawer state
  handleOpen() {
    super.handleOpen();
    // Only dispatch header transparency events on desktop
    if (window.matchMedia('(min-width: 1024px)').matches) {
      document.dispatchEvent(new CustomEvent('header:setActive'));
    }
  }

  // Override handleClose to announce header drawer state
  handleClose() {
    super.handleClose();
    // Only dispatch header transparency events on desktop
    if (window.matchMedia('(min-width: 1024px)').matches) {
      document.dispatchEvent(new CustomEvent('header:setInactive'));
    }
  }
}

// Cart Drawer Component - extends generic drawer
class CartDrawerComponent extends DrawerComponent {
  connectedCallback() {
    super.connectedCallback();
    this.removeButtons = document.querySelectorAll("[data-cart-remove]");
    this.#attachRemoveListeners();
    this.#setupSpecificListeners();
  }

  getToggleSelector() {
    return "[data-drawer-toggle]";
  }

  getCloseSelector() {
    return "[data-drawer-toggle]";
  }

  getAnimationType() {
    return 'slide';
  }

  getEventPrefix() {
    return "cart";
  }

  #setupSpecificListeners() {
    this.#handlePriceUpdate();
    this.#attachUpsellListeners();
    this.#initializeUpsellSwiper();
    window.addEventListener("cart:item-added", this.#handleItemAdded);
    window.addEventListener("cart:updated", this.#handleCartUpdated);
  }

  // Override handleOpen to announce cart drawer state
  handleOpen() {
    super.handleOpen();
    // Only dispatch header transparency events on desktop
    if (window.matchMedia('(min-width: 1024px)').matches) {
      document.dispatchEvent(new CustomEvent('header:setActive'));
    }
  }

  // Override handleClose to announce cart drawer state
  handleClose() {
    super.handleClose();
    // Only dispatch header transparency events on desktop
    if (window.matchMedia('(min-width: 1024px)').matches) {
      document.dispatchEvent(new CustomEvent('header:setInactive'));
    }
  }

  #handlePriceUpdate() {
    const totalPrice = document.querySelector("[data-cart-total-price]");
    if (totalPrice) {
      // Price update logic can be added here
    }
  }

  #attachRemoveListeners() {
    // Re-query buttons to get current ones after content updates
    this.removeButtons = this.querySelectorAll("[data-cart-remove]");
    this.removeButtons.forEach((btn) => {
      btn.addEventListener("click", this.#handleRemoveItem);
    });
  }

  #attachUpsellListeners() {
    // Re-query upsell forms to get current ones after content updates
    const upsellForms = this.querySelectorAll("[data-cart-upsell] .cart-upsell__form");
    upsellForms.forEach((form) => {
      form.addEventListener("submit", this.#handleUpsellAddToCart);
    });
  }

  #initializeUpsellSwiper() {
    // Trigger a custom event that the swiper.js module can listen for
    const event = new CustomEvent('cart:upsell-swiper-init', {
      detail: { container: this }
    });
    document.dispatchEvent(event);
  }

  #handleUpsellAddToCart = async (event) => {
    event.preventDefault();

    const form = event.currentTarget;
    const variantId = form.querySelector('input[name="id"]').value;
    const quantityInput = form.querySelector('quantity-selector-component input[name="quantity"]');
    const quantity = parseInt(quantityInput?.value) || 1;
    const button = form.querySelector('[data-cart-upsell-add]');

    if (!variantId || !button) return;

    // Collect any line item properties from hidden inputs
    const properties = {};
    form.querySelectorAll('input[name^="properties["]').forEach(input => {
      const match = input.name.match(/properties\[(.*)\]/);
      if (match) {
        properties[match[1]] = input.value;
      }
    });

    // Update button state
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      const requestBody = {
        id: variantId,
        quantity: quantity,
      };
      
      // Only add properties if there are any
      if (Object.keys(properties).length > 0) {
        requestBody.properties = properties;
      }
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const item = await response.json();
        window.dispatchEvent(
          new CustomEvent('cart:item-added', { detail: item })
        );

        // Show success feedback
        button.textContent = 'Added!';

        // Reset quantity to 1 and restore button after success
        setTimeout(() => {
          if (quantityInput) quantityInput.value = 1;
          button.textContent = originalText;
          button.disabled = false;
        }, 1000);
      } else {
        throw new Error('Failed to add item to cart');
      }
    } catch (error) {
      console.error('Error adding upsell item:', error);

      // Reset button on error
      button.textContent = originalText;
      button.disabled = false;

      // You could add error handling/notification here
    }
  };

  #handleRemoveItem = async (event) => {
    const line = event.currentTarget.dataset.line;
    if (!line) return;

    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: line,
          quantity: 0,
        }),
      });

      if (response.ok) {
        const cart = await response.json();
        // Update cached cart data before dispatching event
        window.__cart = cart;
        window.dispatchEvent(
          new CustomEvent("cart:updated", { detail: cart })
        );
        await this.#renderCartContents();
      }
    } catch (error) {
      console.error("Error removing item:", error);
    }
  };

  #renderCartContents = async () => {
    try {
      const response = await fetch("/?section_id=cart-drawer");
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // Update cart contents
      const newContents = doc.querySelector("[data-cart-drawer-contents]");
      const currentContents = this.querySelector("[data-cart-drawer-contents]");

      if (newContents && currentContents) {
        currentContents.innerHTML = newContents.innerHTML;
        this.#attachRemoveListeners();
      }

      // Update cart footer (including upsell component)
      const newFooter = doc.querySelector("cart-drawer-footer");
      const currentFooter = this.querySelector("cart-drawer-footer");

      if (newFooter && currentFooter) {
        currentFooter.innerHTML = newFooter.innerHTML;
        this.#attachUpsellListeners();
        this.#initializeUpsellSwiper();
      }

      updateCartBubble();
    } catch (error) {
      console.error("Error rendering cart contents:", error);
    }
  };

  #handleItemAdded = async (event) => {
    // Mark that cart has been modified (for navigation sync)
    sessionStorage.setItem('cartModified', 'true');
    
    // Preserve accordion states before re-rendering
    const cartNotesState = this.#preserveCartNotesState();
    const cartUpsellState = this.#preserveCartUpsellState();
    
    await this.#renderCartContents();
    
    // Re-initialize and restore accordion states after rendering
    if (typeof window.initCartNotes === 'function') {
      window.initCartNotes();
      this.#restoreCartNotesState(cartNotesState);
    }
    if (typeof window.initCartUpsellAccordion === 'function') {
      window.initCartUpsellAccordion();
      this.#restoreCartUpsellState(cartUpsellState);
    }
    
    // Don't open cart drawer for buy now submissions
    const isBuyNow = event.detail?.isBuyNow;
    if (!isBuyNow) {
      this.handleOpen();
    }
    
    updateCartBubble();
  };

  #handleCartUpdated = async (event) => {
    // Mark that cart has been modified (for navigation sync)
    sessionStorage.setItem('cartModified', 'true');
    
    // Preserve accordion states before re-rendering
    const cartNotesState = this.#preserveCartNotesState();
    const cartUpsellState = this.#preserveCartUpsellState();
    
    await this.#renderCartContents();
    
    // Re-initialize and restore accordion states after rendering
    if (typeof window.initCartNotes === 'function') {
      window.initCartNotes();
      this.#restoreCartNotesState(cartNotesState);
    }
    if (typeof window.initCartUpsellAccordion === 'function') {
      window.initCartUpsellAccordion();
      this.#restoreCartUpsellState(cartUpsellState);
    }
    
    updateCartBubble();
  };

  #preserveCartNotesState() {
    const cartNotesComponent = this.querySelector('[data-cart-notes]');
    if (!cartNotesComponent) return null;
    
    const toggle = cartNotesComponent.querySelector('[data-cart-notes-toggle]');
    const textarea = cartNotesComponent.querySelector('textarea');
    return {
      isExpanded: toggle?.getAttribute('aria-expanded') === 'true',
      noteValue: textarea?.value || ''
    };
  }

  #restoreCartNotesState(state) {
    if (!state) return;
    
    const cartNotesComponent = this.querySelector('[data-cart-notes]');
    if (!cartNotesComponent) return;
    
    const toggle = cartNotesComponent.querySelector('[data-cart-notes-toggle]');
    const content = cartNotesComponent.querySelector('[data-cart-notes-content]');
    const textarea = cartNotesComponent.querySelector('textarea');
    
    if (state.isExpanded && toggle && content) {
      toggle.setAttribute('aria-expanded', 'true');
      content.hidden = false;
    }
    
    if (state.noteValue && textarea) {
      textarea.value = state.noteValue;
    }
  }

  #preserveCartUpsellState() {
    const upsellComponent = this.querySelector('[data-cart-upsell]');
    if (!upsellComponent) return null;

    const toggle = upsellComponent.querySelector('[data-cart-upsell-toggle]');
    return {
      isExpanded: toggle?.getAttribute('aria-expanded') === 'true'
    };
  }

  #restoreCartUpsellState(state) {
    if (!state) return;

    const upsellComponent = this.querySelector('[data-cart-upsell]');
    if (!upsellComponent) return;

    const toggle = upsellComponent.querySelector('[data-cart-upsell-toggle]');
    const content = upsellComponent.querySelector('[data-cart-upsell-content]');
    if (!toggle || !content) return;

    if (state.isExpanded) {
      toggle.setAttribute('aria-expanded', 'true');
      content.hidden = false;
    } else {
      toggle.setAttribute('aria-expanded', 'false');
      content.hidden = true;
    }
  }
}


document.addEventListener("DOMContentLoaded", () => {
  if (!customElements.get("cart-drawer-component")) {
    customElements.define("cart-drawer-component", CartDrawerComponent);
  }
});

// Sync cart drawer on back/forward navigation
// Note: Clear-Site-Data headers may prevent bfcache, so we check navigation type instead of event.persisted
async function syncCartDrawerIfNeeded() {
  // Skip entirely if cart was never modified this session
  if (!sessionStorage.getItem('cartModified')) return;
  
  const cartDrawer = document.querySelector('cart-drawer-component');
  if (!cartDrawer) return;
  
  // Preserve accordion states before checking/syncing
  const preserveCartNotesState = () => {
    const cartNotesComponent = cartDrawer.querySelector('[data-cart-notes]');
    if (!cartNotesComponent) return null;
    
    const toggle = cartNotesComponent.querySelector('[data-cart-notes-toggle]');
    const textarea = cartNotesComponent.querySelector('textarea');
    return {
      isExpanded: toggle?.getAttribute('aria-expanded') === 'true',
      noteValue: textarea?.value || ''
    };
  };
  
  const restoreCartNotesState = (state) => {
    if (!state) return;
    
    const cartNotesComponent = cartDrawer.querySelector('[data-cart-notes]');
    if (!cartNotesComponent) return;
    
    const toggle = cartNotesComponent.querySelector('[data-cart-notes-toggle]');
    const content = cartNotesComponent.querySelector('[data-cart-notes-content]');
    const textarea = cartNotesComponent.querySelector('textarea');
    
    if (state.isExpanded && toggle && content) {
      toggle.setAttribute('aria-expanded', 'true');
      content.hidden = false;
    }
    
    if (state.noteValue && textarea) {
      textarea.value = state.noteValue;
    }
  };

  const preserveCartUpsellState = () => {
    const upsellComponent = cartDrawer.querySelector('[data-cart-upsell]');
    if (!upsellComponent) return null;

    const toggle = upsellComponent.querySelector('[data-cart-upsell-toggle]');
    return {
      isExpanded: toggle?.getAttribute('aria-expanded') === 'true'
    };
  };

  const restoreCartUpsellState = (state) => {
    if (!state) return;

    const upsellComponent = cartDrawer.querySelector('[data-cart-upsell]');
    if (!upsellComponent) return;

    const toggle = upsellComponent.querySelector('[data-cart-upsell-toggle]');
    const content = upsellComponent.querySelector('[data-cart-upsell-content]');
    if (!toggle || !content) return;

    if (state.isExpanded) {
      toggle.setAttribute('aria-expanded', 'true');
      content.hidden = false;
    } else {
      toggle.setAttribute('aria-expanded', 'false');
      content.hidden = true;
    }
  };
  
  const cartNotesState = preserveCartNotesState();
  const cartUpsellState = preserveCartUpsellState();
  
  // Check if cart drawer needs sync by comparing displayed vs actual cart
  const displayedItems = cartDrawer.querySelector('[data-cart-drawer-contents]')?.querySelectorAll('li').length || 0;
  
  try {
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    const actualItems = cart.item_count || 0;
    
    // Update cached cart data for bubble
    window.__cart = cart;
    
    // Only re-render if there's a mismatch
    if (displayedItems !== actualItems) {
      const sectionResponse = await fetch('/?section_id=cart-drawer');
      const html = await sectionResponse.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const newContents = doc.querySelector('[data-cart-drawer-contents]');
      const currentContents = cartDrawer.querySelector('[data-cart-drawer-contents]');
      if (newContents && currentContents) {
        currentContents.innerHTML = newContents.innerHTML;
      }
      
      const newFooter = doc.querySelector('cart-drawer-footer');
      const currentFooter = cartDrawer.querySelector('cart-drawer-footer');
      if (newFooter && currentFooter) {
        currentFooter.innerHTML = newFooter.innerHTML;
      }
      
      // Re-initialize accordion components
      if (typeof window.initCartNotes === 'function') {
        window.initCartNotes();
      }
      if (typeof window.initCartUpsellAccordion === 'function') {
        window.initCartUpsellAccordion();
      }
      
      // Restore accordion states
      restoreCartNotesState(cartNotesState);
      restoreCartUpsellState(cartUpsellState);
    }
    
    // Always update cart bubble (even if drawer doesn't need sync)
    updateCartBubble();
    
  } catch (error) {
    console.error('Error syncing cart drawer:', error);
  }
}

// Handle back/forward navigation (works even if bfcache is disabled by Clear-Site-Data)
window.addEventListener('pageshow', async (event) => {
  // Check if this is back/forward navigation using Performance API
  const perfEntries = performance.getEntriesByType('navigation');
  const isBackForward = perfEntries.length > 0 && perfEntries[0].type === 'back_forward';
  
  // Also check legacy API for older browsers
  const isBackForwardLegacy = performance.navigation && performance.navigation.type === 2;
  
  if (isBackForward || isBackForwardLegacy || event.persisted) {
    await syncCartDrawerIfNeeded();
  }
});

// Also check on initial page load if it was from back/forward navigation
document.addEventListener('DOMContentLoaded', async () => {
  const perfEntries = performance.getEntriesByType('navigation');
  const isBackForward = perfEntries.length > 0 && perfEntries[0].type === 'back_forward';
  
  if (isBackForward) {
    // Wait for cart drawer to be initialized
    setTimeout(() => syncCartDrawerIfNeeded(), 100);
  }
});

document.addEventListener("DOMContentLoaded", () => {
  if (!customElements.get("header-drawer-component")) {
    customElements.define("header-drawer-component", HeaderDrawerComponent);
  }
});

// Filters Drawer Component - extends generic drawer
class FiltersDrawerComponent extends DrawerComponent {
  connectedCallback() {
    super.connectedCallback();
    this.clearAllButton = this.querySelector('[data-filters-clear-all]');
    this.setupFiltersSpecific();
  }

  getToggleSelector() {
    return "[data-filters-drawer-toggle]";
  }

  getCloseSelector() {
    return "[data-filters-drawer-toggle]";
  }

  getAnimationType() {
    return 'slide';
  }

  getEventPrefix() {
    return "filters";
  }

  setupFiltersSpecific() {
    if (this.clearAllButton) {
      this.clearAllButton.addEventListener('click', this.handleClearAll.bind(this));
    }

    // Handle filter changes
    this.addEventListener('change', this.handleFilterChange.bind(this));
  }

  handleClearAll() {
    // Clear all filter inputs
    const inputs = this.querySelectorAll('input[type="checkbox"], input[type="number"]');
    inputs.forEach(input => {
      if (input.type === 'checkbox') {
        input.checked = false;
      } else if (input.type === 'number') {
        input.value = '';
      }
    });

    // Trigger form change to update filters
    const form = this.querySelector('form');
    if (form) {
      form.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  handleFilterChange(event) {
    // Debounce filter changes for better performance
    clearTimeout(this.filterTimeout);
    this.filterTimeout = setTimeout(() => {
      // Update active filters display if needed
      this.updateActiveFiltersDisplay();
    }, 100);
  }

  updateActiveFiltersDisplay() {
    // This could be expanded to show active filters in the drawer
    const activeFiltersContainer = this.querySelector('active-filters');
    // Implementation would depend on how you want to display active filters
  }
}

document.addEventListener("DOMContentLoaded", () => {
  if (!customElements.get("filters-drawer-component")) {
    customElements.define("filters-drawer-component", FiltersDrawerComponent);
  }
});

// Make DrawerComponent globally available for extension by other components
window.DrawerComponent = DrawerComponent;

class LocalizationForm extends Component {
  constructor() {
    super();
    this.elements = {
      input: this.querySelector('input[name="country_code"]'),
      button: this.querySelector('.localization-selector__button'),
      panel: this.querySelector('.localization-selector__list'),
    };

    this.elements.button.addEventListener('click', this.openSelector.bind(this));
    this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

    this.querySelectorAll('a').forEach(item => item.addEventListener('click', this.onItemClick.bind(this)));

    // Close dropdown when clicking outside
    document.addEventListener('click', this.onDocumentClick.bind(this));
  }

  hidePanel() {
    // Only trigger event if panel is actually visible (going from open to closed)
    const wasVisible = !this.elements.panel.hasAttribute('hidden') && !this.elements.panel.classList.contains('hidden');
    
    this.elements.button.setAttribute('aria-expanded', 'false');
    this.elements.panel.setAttribute('hidden', true);
    this.elements.panel.classList.add('hidden');
    
    // Only dispatch closed event if panel was actually visible
    if (wasVisible && window.matchMedia('(min-width: 1024px)').matches) {
      document.dispatchEvent(new CustomEvent('header:setInactive'));
    }
  }

  showPanel() {
    this.elements.button.setAttribute('aria-expanded', 'true');
    this.elements.panel.removeAttribute('hidden');
    this.elements.panel.classList.remove('hidden');
    // Use generic header events for consistency (desktop only)
    if (window.matchMedia('(min-width: 1024px)').matches) {
      document.dispatchEvent(new CustomEvent('header:setActive'));
    }
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    this.hidePanel();
    this.elements.button.focus();
  }

  onItemClick(event) {
    event.preventDefault();
    const form = this.querySelector('form');
    this.elements.input.value = event.currentTarget.dataset.value;
    if (form) form.submit();
  }

  openSelector() {
    this.elements.button.focus();
    const isExpanded = this.elements.button.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      this.hidePanel();
    } else {
      this.showPanel();
    }
  }

  closeSelector(event) {
    const shouldClose = event.relatedTarget && !this.contains(event.relatedTarget);
    if (event.relatedTarget === null || shouldClose) {
      this.hidePanel();
    }
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.hidePanel();
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  if (!customElements.get("localization-form")) {
    customElements.define("localization-form", LocalizationForm);
  }
});



// Function to set header height as CSS custom property
function setHeaderHeight() {
  const header = document.querySelector('.site-header');
  if (header) {
    const headerHeight = header.getBoundingClientRect().height;
    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
  }
}

function setHeaderGroupHeight() {
  const headerGroup = document.querySelector('#header-group');
  if (headerGroup) {
    const headerGroupHeight = headerGroup.getBoundingClientRect().height;
    document.documentElement.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
  }
}

// Function to initialize overlay functionality
function initializeOverlay() {
  const overlay = document.querySelector('[data-header-overlay]');
  const allDetails = document.querySelectorAll('header details');

  allDetails.forEach(details => {
    const summary = details.querySelector('summary');

    summary?.addEventListener('click', (e) => {
      allDetails.forEach(other => {
        if (other !== details) other.removeAttribute('open');
      });

      setTimeout(() => {
        const isOpen = details.hasAttribute('open');
        if (isOpen) {
          overlay?.classList.remove('pointer-events-none', 'opacity-0');
          overlay?.classList.add('opacity-100');
        } else {
          overlay?.classList.add('opacity-0');
          overlay?.classList.remove('opacity-100');
          setTimeout(() => {
            overlay?.classList.add('pointer-events-none');
          }, 300);
        }
      }, 10);
    });
  });

  overlay?.addEventListener('click', () => {
    overlay.classList.remove('opacity-100');
    setTimeout(() => {
      overlay.classList.add('pointer-events-none');
    }, 300);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  setHeaderHeight();
  setHeaderGroupHeight();
  initializeOverlay();
});

// Update header height on window resize
window.addEventListener('resize', () => {
  setHeaderHeight();
  setHeaderGroupHeight();
});

// Reinitialize overlay, header height, and scroll rotate elements after page transitions
document.addEventListener('pageTransitionComplete', () => {
  setHeaderHeight();
  setHeaderGroupHeight();
  initializeOverlay();
});

document.addEventListener('DOMContentLoaded', function () {
  // Function to open popup with fade animation
  function openPopup(popup) {
    const content = popup.querySelector('.popup-content-container');

    // Show popup immediately (but invisible)
    popup.classList.remove('hidden');
    popup.classList.add('flex');

    // Force reflow to ensure hidden->flex change is applied
    popup.offsetHeight;

    // Trigger fade-in animation
    popup.classList.remove('opacity-0');
    popup.classList.add('opacity-100');

    if (content) {
      content.classList.remove('scale-95');
      content.classList.add('scale-100');
    }

    // Focus the close button for accessibility
    const closeButton = popup.querySelector('[data-popup-close]');
    if (closeButton) {
      closeButton.focus();
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';
  }

  // Function to close popup with fade animation
  function closePopup(popup, returnFocusTo = null) {
    const content = popup.querySelector('.popup-content-container');

    // Trigger fade-out animation
    popup.classList.remove('opacity-100');
    popup.classList.add('opacity-0');

    if (content) {
      content.classList.remove('scale-100');
      content.classList.add('scale-95');
    }

    // Wait for animation to complete, then hide
    setTimeout(() => {
      popup.classList.add('hidden');
      popup.classList.remove('flex');

      // Restore body scroll
      document.body.style.overflow = '';

      // Return focus to trigger or specified element
      if (returnFocusTo) {
        returnFocusTo.focus();
      }
    }, 300); // Match the CSS transition duration
  }

  // Handle popup triggers
  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('[data-popup-trigger]');
    if (trigger) {
      e.preventDefault();
      const popupId = trigger.getAttribute('data-popup-trigger');
      const popup = document.getElementById(popupId);
      if (popup) {
        openPopup(popup);
      }
    }
  });

  // Handle popup close buttons
  document.addEventListener('click', function (e) {
    const closeButton = e.target.closest('[data-popup-close]');
    if (closeButton) {
      e.preventDefault();
      const popupId = closeButton.getAttribute('data-popup-close');
      const popup = document.getElementById(popupId);
      if (popup) {
        const trigger = document.querySelector(`[data-popup-trigger="${popupId}"]`);
        closePopup(popup, trigger);
      }
    }
  });

  // Handle backdrop clicks
  document.addEventListener('click', function (e) {
    if (e.target.matches('[data-popup-modal]')) {
      const popup = e.target;
      const popupId = popup.getAttribute('data-popup-modal');
      const trigger = document.querySelector(`[data-popup-trigger="${popupId}"]`);
      closePopup(popup, trigger);
    }
  });

  // Handle escape key
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      const openPopup = document.querySelector('.popup-modal.flex:not(.opacity-0)');
      if (openPopup) {
        const popupId = openPopup.getAttribute('data-popup-modal');
        const trigger = document.querySelector(`[data-popup-trigger="${popupId}"]`);
        closePopup(openPopup, trigger);
      }
    }
  });
});

  // Handle lazy loading with fade-in effect for image snippet
  function initializeImageSnippetLazyImages() {
    const lazyImages = document.querySelectorAll('.image-snippet img[loading="lazy"]:not([data-observer-attached])');

    if (lazyImages.length === 0) return;

    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target;

          // If image is already loaded, show it immediately
          if (img.complete) {
            img.classList.add('loaded');
          } else {
            // Otherwise wait for load event
            img.addEventListener('load', function () {
              if (!img.classList.contains('loaded')) {
                img.classList.add('loaded');
              }
            });

            // Fallback: if load event doesn't fire, show after a delay
            setTimeout(() => {
              if (!img.classList.contains('loaded')) {
                img.classList.add('loaded');
              }
            }, 1000);
          }

          observer.unobserve(img);
        }
      });
    });

    lazyImages.forEach((img) => {
      img.setAttribute('data-observer-attached', 'true');
      imageObserver.observe(img);
    });
  }

  // Initialize on DOM ready
  document.addEventListener('DOMContentLoaded', initializeImageSnippetLazyImages);

  // Re-initialize on page transitions (for SPAs)
  document.addEventListener('pageTransitionComplete', initializeImageSnippetLazyImages);

  // For Shopify section loading
  document.addEventListener('shopify:section:load', initializeImageSnippetLazyImages);

  // Re-initialize when filters are updated
  document.addEventListener('filtersUpdated', initializeImageSnippetLazyImages);